import { lazy, Suspense, useState, useEffect } from "react";
import { Link, Outlet, useLocation, useMatch } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/components/contexts/AuthContext/UseAuth";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Logo } from "@/components/ui/Logo";
import { landlordApi } from "@/api/LandlordApi";
import { useAgentProfile } from "@/hooks/useAgentProfile";
import { Button } from "@/components/ui/shadcn/button";
import { UserType } from "@/types/IdentityTypes";
import { CommandWorkspaceLayout } from "@/components/layouts/CommandWorkspaceLayout";
import { landlordNavLinks } from "@/components/navigation/navConfig";
import { AskHazel } from "@/components/hazel/AskHazel";
import { useReport } from "@/hooks/useReports";
import { LockKeyhole } from "lucide-react";

const LandlordLayout = lazy(() => import("@/pages/Landlord/LandlordLayout"));
const AgentLayout = lazy(() => import("@/pages/Agent/AgentLayout"));

export default function ReportsWorkspace() {
  const { isAuthenticated, user, login } = useAuth();
  const location = useLocation();
  const needsAccount =
    !isAuthenticated &&
    (location.pathname.startsWith("/views") ||
      /^\/reports\/?$/.test(location.pathname));
  const destination = location.pathname + location.search + location.hash;
  useEffect(() => {
    if (needsAccount) void login(destination);
  }, [needsAccount, login, destination]);
  if (needsAccount) return <LoadingScreen />;
  if (!isAuthenticated) return <PublicReportWorkspace />;
  const role = user?.profile.UserType;
  if (isAuthenticated && role === UserType.Landlord)
    return <LandlordReportsWorkspace />;
  if (
    isAuthenticated &&
    [UserType.Manager, UserType.Agent, UserType.AgencyManager].includes(
      role as UserType,
    )
  )
    return <AgentReportsWorkspace />;
  return <ReportFrame />;
}

function PublicReportWorkspace() {
  const { login } = useAuth();
  const location = useLocation();
  const match = useMatch("/reports/:reportId");
  const { data: report } = useReport(match?.params.reportId);
  const destination = location.pathname + location.search + location.hash;
  const hazel = (
    <AskHazel
      appearance="toolbar"
      subject={report ? `the report “${report.title}”` : "this report"}
      details={
        report
          ? `Report ID: ${report.id} · ${report.category} · Published ${report.publishedAt}`
          : undefined
      }
    />
  );
  return (
    <CommandWorkspaceLayout
      title="Your workspace"
      navItems={landlordNavLinks}
      topActions={hazel}
      mobileTopActions={hazel}
      navigationGate={
        <section
          aria-label="Workspace access"
          className="rounded-2xl border border-white/20 bg-slate-800/75 p-4 text-white shadow-xl backdrop-blur-xl"
        >
          <div className="mb-4 flex size-10 items-center justify-center rounded-xl border border-white/20 bg-white/10">
            <LockKeyhole aria-hidden="true" className="size-5" />
          </div>
          <h2 className="text-base font-semibold leading-snug">
            Sign in to your workspace
          </h2>
          <p className="mt-2 text-sm leading-5 text-slate-300">
            Your properties, rent and maintenance, together.
          </p>
          <Button
            className="mt-5"
            onClick={() => void login(destination, undefined, "signin")}
          >
            Sign in
          </Button>
          {report?.hasAccount === false && (
            <Button
              variant="link"
              className="mt-1 text-slate-100"
              onClick={() => void login(destination, undefined, "signup")}
            >
              Create an account
            </Button>
          )}
        </section>
      }
    >
      <Outlet />
    </CommandWorkspaceLayout>
  );
}

// Profile availability only affects the reports shell, never access to report content.
function LandlordReportsWorkspace() {
  const profile = useQuery({
    queryKey: ["landlord-profile"],
    queryFn: () => landlordApi.getProfile(),
    retry: false,
    staleTime: Infinity,
  });
  if (profile.isPending) return <LoadingScreen />;
  if (profile.isError || !profile.data) return <ReportFrame />;
  return (
    <Suspense fallback={<LoadingScreen />}>
      <LandlordLayout />
    </Suspense>
  );
}

function AgentReportsWorkspace() {
  const profile = useAgentProfile({ retry: false });
  if (profile.isPending) return <LoadingScreen />;
  if (profile.isError || !profile.data) return <ReportFrame />;
  return (
    <Suspense fallback={<LoadingScreen />}>
      <AgentLayout />
    </Suspense>
  );
}

function ReportFrame() {
  const { isAuthenticated, user, logout } = useAuth();
  const [signOutError, setSignOutError] = useState("");
  const cache = useQueryClient();
  return (
    <div className="min-h-screen bg-bg-dark text-foreground">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-border px-4 py-4 sm:px-8">
        <Link to="/reports" aria-label="Rentinit reports">
          <Logo className="h-7 w-auto" />
        </Link>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {isAuthenticated && user?.profile.email && (
            <span
              className="max-w-[240px] truncate text-sm text-muted-foreground"
              title={user.profile.email}
            >
              {user.profile.email}
            </span>
          )}
          {isAuthenticated ? (
            <Button
              className="w-auto shrink-0"
              variant="ghost"
              onClick={async () => {
                setSignOutError("");
                try {
                  await logout();
                  cache.removeQueries({ queryKey: ["reports"] });
                } catch {
                  setSignOutError(
                    "We couldn’t finish signing out. Please try again.",
                  );
                }
              }}
            >
              Sign out
            </Button>
          ) : null}
        </div>
      </header>
      {signOutError && (
        <p role="alert" className="px-4 py-3 text-sm text-destructive">
          {signOutError}
        </p>
      )}
      <Outlet />
    </div>
  );
}
